import { ActivatedRouteSnapshot, ResolveFn, Routes } from '@angular/router';
import { OktaAuthGuard } from '@okta/okta-angular';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { permissionGuard } from './app-core/guards/permission.guard';
import { sessionGuard } from './app-core/guards/session.guard';
import { PermissionType } from './app-core/models/permission/permission-type-enum';
import { LoginCallback } from './app-core/ui/login-callback/login-callback';


export const titleResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    const translate = inject(TranslateService);
    const path = route.pathFromRoot
        .flatMap(v => v.url.map(segment => segment.path))
        .filter(segment => isNaN(Number(segment))) // remove numbered segments
    return path.map(m => translate.instant(`title.${m}`)).join(' | ');
};

export const routes: Routes = [
    {
        path: 'help',
        loadComponent: () => import('./app-core/ui/help/help'),
        title: titleResolver,
        canActivate: [OktaAuthGuard],
    },
    {
        path: 'help/:item',
        loadComponent: () => import('./app-core/ui/help/help'),
        title: titleResolver,
        canActivate: [OktaAuthGuard],
    },
    {
        // Language and colour theme moved into the profile menu of the app header.
        path: 'preferences',
        redirectTo: 'home',
    },
    {
        path: 'home',
        loadComponent: () => import('./features/home/home'),
        title: titleResolver,
        canActivate: [OktaAuthGuard],
    },
    {
        path: 'requests',
        loadComponent: () => import('./features/requests/requests'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, permissionGuard],
        data: { expectedPermissions: [PermissionType.AccessRequests] },
    },
    {
        path: 'add/manual',
        loadComponent: () => import('./features/parts/add-part/add-part'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, permissionGuard],
        // A focus page: the form has its own header with Close and Submit part, without app navigation.
        data: { expectedPermissions: [PermissionType.UpdatePartNumbers, PermissionType.UpdateMpns], focus: true },
    },
    {
        path: 'add/import',
        loadComponent: () => import('./features/import/import-wizard/import-wizard'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, permissionGuard],
        data: { expectedPermissions: [PermissionType.UpdatePartNumbers, PermissionType.UpdateMpns] },
        // Focus pages like manual entry: their own header with Close, the step and Next, without app navigation.
        // The wizard holds the session of the import, so moving between the steps keeps the file.
        children: [
            {
                path: '',
                loadComponent: () => import('./features/import/import-from-file/import-from-file'),
                data: { focus: true },
            },
            {
                path: 'header-row',
                loadComponent: () => import('./features/import/import-header-row/import-header-row'),
                title: titleResolver,
                data: { focus: true },
            },
        ],
    },
    {
        path: 'config',
        loadComponent: () => import('./features/config/config'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, sessionGuard],
    },
    {
        path: 'admin',
        loadComponent: () => import('./features/admin/admin'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, permissionGuard],
        data: { expectedRoles: ['Admin'] },
    },
    {
        path: 'api-keys',
        loadComponent: () => import('./features/api-keys/my-api-keys/my-api-keys'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, sessionGuard],
    },

    {
        path: 'sap-token',
        loadComponent: () => import('./features/sap-token/sap-token'),
        title: titleResolver,
        canActivate: [OktaAuthGuard, sessionGuard],
    },

    {
        // Renders without the toolbar and navigation: its only content is the sign-in outcome.
        path: 'login/callback',
        component: LoginCallback,
        data: { standalone: true },
    },
    {
        path: '',
        redirectTo: '/home',
        pathMatch: 'full'
    },
];
